// RowContext — carries the "current product row" down to the row-bound block
// components (product-image / -name / -parameter / -sku / -quantity). It is the
// React-side mirror of the PDF renderer's `_currentRow` field: ProductTableBlock
// wraps each table cell (and each group header) in a <RowContext.Provider> bound
// to the row being rendered, and the product-* blocks read it back via
// useCurrentRow(). Outside a table cell the context is null, so a stray
// product-* block placed in a normal slot simply renders nothing.
//
// fieldKey() reproduces the C# PdfSummaryRow.FieldKey vocabulary verbatim so
// the table's sort/group semantics match the PDF byte-for-byte: name, sku,
// quantity (zero-padded so string-compare orders numerically), category, series,
// group (groupName ?? groupId), and param:<key> for any product parameter.

import { createContext, useContext } from "react";

/**
 * One product row in a product-table — the resolved, display-ready projection
 * of a GroupedRow (see api/pdfRender.ts). productId is the *display SKU* the
 * server resolves (honours material picks + manual overrides), so it starts ""
 * and gets filled in once the SKU-resolution call returns.
 */
export interface ProductRow {
  /** Display SKU (resolved), may be "" while the lookup is in flight or failed. */
  productId: string;
  name: string;
  imageUrl?: string;
  quantity: number;
  /** All of the product's parameter values (from product.parameterValues ?? {}). */
  parameters: Record<string, string>;
  category?: string;
  series?: string;
  groupId?: string;
  groupName?: string;
  /** The user's chosen finishes for this row, resolved to display names. One
   *  entry per surface the user actually picked a material for (locked /
   *  default-only surfaces are omitted), in surface order. Empty when nothing
   *  was picked or the product detail hasn't resolved yet. Drives the
   *  product-materials block. */
  materials: RowMaterial[];
  /** Formatted prices from the live breakdown (e.g. "1.234,00 kr"). Undefined
   *  when the host has no price source or the row couldn't be priced — the
   *  product-price block renders nothing. */
  unitPrice?: string;
  lineTotal?: string;
  /** Units covered by an inclusion allowance — visible but not charged. */
  includedQuantity?: number;
}

/** A resolved finish pick: the surface's display label + the chosen material's
 *  display name (e.g. { surfaceLabel: "Wood", materialName: "Oak" }). */
export interface RowMaterial {
  surfaceLabel: string;
  materialName: string;
}

/** Null outside a product-table cell / group header. */
export const RowContext = createContext<ProductRow | null>(null);

/** Read the current row. Null when not rendered inside a product-table cell —
 *  the product-* blocks treat that as "render nothing". */
export function useCurrentRow(): ProductRow | null {
  return useContext(RowContext);
}

/**
 * Resolve a sort/group key by field name — mirrors the C# PdfSummaryRow.FieldKey
 * exactly. Recognises the built-in fields (name, sku, quantity, category, series,
 * group) and `param:<key>` for an arbitrary parameter; unknown fields resolve to
 * "" so they sort stably. Quantity is zero-padded to 9 digits so a plain string
 * compare orders it numerically.
 */
export function fieldKey(row: ProductRow, field: string): string {
  if (field.toLowerCase().startsWith("param:")) {
    const key = field.slice("param:".length);
    return row.parameters[key] ?? "";
  }
  switch (field) {
    case "name":
      return row.name;
    case "sku":
      return row.productId;
    case "quantity":
      return String(row.quantity).padStart(9, "0");
    case "category":
      return row.category ?? "";
    case "series":
      return row.series ?? "";
    case "group":
      return row.groupName ?? row.groupId ?? "";
    default:
      return "";
  }
}
